import {
  getCategoryGridActionId,
  getChoicePresets,
  getCommitPresets,
  getPresetPresentationType,
  isGenerationPresetAction,
  isVibeBundlePreset,
  orderPresets,
} from "@/lib/agent/presetPresentation";
import {
  getRendererPresentedMessagePresets,
  getRendererPresentedWidgetPresets,
  isFrontReviewDecisionPresetSurface,
} from "@/lib/agent/visiblePresetCapabilities";
import {
  isDesignerRenderActionId,
  type DesignerRenderActionId,
} from "@/lib/agent/designerActionPresentation";
import { buildGenerateFooterPreset } from "@/lib/agent/footerActions";
import { normalizeWidgetType } from "@/lib/agent/widgetPlan";
import { getWidgetPresets } from "@/lib/agent/widgetPresets";
import type {
  AgentActionPayload,
  AgentPhase,
  AgentWidget,
  PresetOption,
} from "@/types/createAgent";
import type { DesignerAction, DesignerChoice } from "./contracts";

export type DesignerReviewPanel = "main" | "modify" | "back";

function stableValue(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableValue).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => `${JSON.stringify(key)}:${stableValue(entry)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

/** Exact payload equality; a duplicate label never identifies a capability. */
export function designerPresetsEqual(
  left: PresetOption,
  right: PresetOption
): boolean {
  return stableValue(left) === stableValue(right);
}

export function designerChoiceKey(choice: DesignerChoice): string {
  return stableValue({ preset: choice.preset, source: choice.source });
}

/** Carry originating coordinates when supplied; ambiguous legacy matches fail closed. */
export function resolveDesignerChoice(
  messageId: string,
  preset: PresetOption,
  widgets: AgentWidget[] = [],
  source?: AgentActionPayload["source"]
): DesignerChoice | null {
  if (source?.widgetId) {
    const widget = widgets.find(
      widget =>
        widget.id === source.widgetId &&
        widget.version === source.widgetVersion &&
        widget.active === true
    );
    return widget &&
      getWidgetPresets(widget).some(candidate =>
        designerPresetsEqual(candidate, preset)
      )
      ? {
          preset,
          source: {
            messageId,
            widgetId: widget.id,
            widgetVersion: widget.version,
          },
        }
      : null;
  }
  const activeWidgets = widgets.filter(widget => widget.active === true);
  const identities = activeWidgets.filter(widget =>
    getWidgetPresets(widget).some(candidate => candidate === preset)
  );
  const matches = identities.length
    ? identities
    : activeWidgets.filter(widget =>
        getWidgetPresets(widget).some(candidate =>
          designerPresetsEqual(candidate, preset)
        )
      );
  if (matches.length > 1) return null;
  const widget = matches[0];
  return {
    preset,
    source: widget
      ? { messageId, widgetId: widget.id, widgetVersion: widget.version }
      : { messageId },
  };
}

export function getDesignerWidgetSelectionFooterChoices(
  widget: AgentWidget
): PresetOption[] {
  if (widget.active !== true) return [];
  const choices = getChoicePresets(getRendererPresentedWidgetPresets(widget));
  if (!choices.length) return [];
  const type = normalizeWidgetType(widget.type);
  const upload = choices.every(
    preset => getPresetPresentationType(preset) === "upload_choice"
  );
  const vibe = choices.every(isVibeBundlePreset);
  const catalog =
    type === "catalog_picker" ||
    Boolean(getCategoryGridActionId(choices) && choices.length > 5);
  const oneTap = type === "field_picker" && !catalog && !upload;
  return !upload && !vibe && !oneTap ? choices : [];
}

export interface DesignerRenderProjection {
  action: DesignerAction | null;
  actionId: DesignerRenderActionId | null;
  unavailableReason: string | null;
  /** Workspace-wide work/restoration block, distinct from a choice requirement. */
  blockedReason?: string | null;
  /** The chat offers options, but none has been chosen yet. */
  awaitingChoice?: boolean;
}

export interface DesignerRenderProjectionInput {
  messageId: string;
  presets: PresetOption[];
  widgets?: AgentWidget[];
  phase: AgentPhase;
  hasFrontImage: boolean;
  selection?: DesignerChoice | null;
  frontReviewPanel?: DesignerReviewPanel;
  blockedReason?: string | null;
}

/** Shared, serializable workspace authority; independent of mounted chat widgets. */
export function projectDesignerRenderAction(
  input: DesignerRenderProjectionInput
): DesignerRenderProjection {
  const { messageId, phase, hasFrontImage, selection, blockedReason } = input;
  const widgets = input.widgets ?? [];
  const ordered = orderPresets(input.presets);
  const reviewPresets = widgets.length
    ? orderPresets(
        widgets
          .filter(widget => widget.active === true)
          .flatMap(getWidgetPresets)
      )
    : ordered;
  const review = isFrontReviewDecisionPresetSurface(reviewPresets);
  const panel = input.frontReviewPanel ?? "main";
  const choices = getChoicePresets(ordered);
  const category = choices.length > 5 ? getCategoryGridActionId(choices) : null;
  const visible = getRendererPresentedMessagePresets(
    ordered,
    selection?.preset.label
  );
  const visibleCommits = getCommitPresets(visible).filter(
    preset => !category || !isGenerationPresetAction(preset.actionId)
  );
  const footerChoices = review
    ? panel === "back"
      ? getChoicePresets(reviewPresets).filter(
          preset =>
            preset.actionId === "set_back_prompt" ||
            preset.actionId === "set_back_style"
        )
      : []
    : widgets.length
      ? widgets.flatMap(getDesignerWidgetSelectionFooterChoices)
      : category
        ? choices
        : [];
  const selected =
    selection?.source.messageId === messageId
      ? (footerChoices
          .map(preset =>
            resolveDesignerChoice(messageId, preset, widgets, selection.source)
          )
          .find(
            choice =>
              choice &&
              designerChoiceKey(choice) === designerChoiceKey(selection)
          ) ?? null)
      : null;
  const fromWidget = (
    widget: AgentWidget,
    preset: PresetOption
  ): DesignerChoice => ({
    preset,
    source: { messageId, widgetId: widget.id, widgetVersion: widget.version },
  });
  const directCandidates: DesignerChoice[] = review
    ? panel === "modify"
      ? widgets.length
        ? widgets
            .filter(widget => widget.active === true)
            .flatMap(widget =>
              getCommitPresets(getWidgetPresets(widget))
                .filter(preset => preset.actionId === "modify_dress_front")
                .map(preset => fromWidget(widget, preset))
            )
        : getCommitPresets(reviewPresets)
            .filter(preset => preset.actionId === "modify_dress_front")
            .map(preset => ({ preset, source: { messageId } }))
      : []
    : widgets.length
      ? widgets.flatMap(widget =>
          getCommitPresets(getRendererPresentedWidgetPresets(widget))
            .filter(
              preset =>
                !getDesignerWidgetSelectionFooterChoices(widget).length ||
                !isGenerationPresetAction(preset.actionId)
            )
            .map(preset => fromWidget(widget, preset))
        )
      : visibleCommits.map(preset => ({ preset, source: { messageId } }));
  const direct = directCandidates.find(choice =>
    isDesignerRenderActionId(choice.preset.actionId)
  );
  const candidateChoice = direct
    ? resolveDesignerChoice(messageId, direct.preset, widgets, direct.source)
    : selected;
  const candidatePreset =
    direct?.preset ??
    (selected
      ? buildGenerateFooterPreset(selected.preset, {
          currentPhase: phase,
          hasFrontImage,
        })
      : null);
  const actionId =
    candidatePreset && isDesignerRenderActionId(candidatePreset.actionId)
      ? candidatePreset.actionId
      : null;
  const expected = footerChoices[0]
    ? buildGenerateFooterPreset(footerChoices[0], {
        currentPhase: phase,
        hasFrontImage,
      })
    : null;
  const suppressed =
    !review && !widgets.length && choices.length && !category
      ? getCommitPresets(ordered).find(
          preset =>
            isDesignerRenderActionId(preset.actionId) &&
            !visibleCommits.some(
              visible => visible.actionId === preset.actionId
            )
        )
      : null;
  const unavailableActionId = expected?.actionId ?? suppressed?.actionId;
  const choiceRequired = footerChoices.length > 0 || Boolean(suppressed);
  const awaitingChoice = !actionId && choiceRequired;
  const unavailableReason =
    blockedReason ??
    (actionId && !candidateChoice
      ? "Choose the action from its original design options."
      : awaitingChoice
        ? "Choose an option in chat first."
        : null);
  const action: DesignerAction | null =
    !unavailableReason && candidateChoice && actionId
      ? {
          ...candidateChoice,
          kind: direct ? "preset" : "selection",
          key: `${direct ? "preset" : "selection"}:${designerChoiceKey(candidateChoice)}`,
        }
      : null;
  return {
    action,
    actionId:
      actionId ??
      (unavailableActionId && isDesignerRenderActionId(unavailableActionId)
        ? unavailableActionId
        : null),
    unavailableReason,
    ...(blockedReason ? { blockedReason } : {}),
    ...(awaitingChoice ? { awaitingChoice: true } : {}),
  };
}
